import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, withQuery } from '@/api/client';
import { useSession } from '@/stores/session';
import { legacyHref } from '@/shell/nav';
import { money, num } from '@/lib/format';
import type { Scope } from './types';
import { DATE_BASIS } from './status';
import type { StatementPayload } from './types';

type Q = Record<string, string | number | boolean | null | undefined>;

export type StatementAction = 'confirm' | 'reopen' | 'lock' | 'unlock' | 'cancel';
export function createStatement(input: { scope: Scope; partnerId: string; month: string; dateBasis: string; note?: string }) {
  return api.post<StatementPayload>('/api/v2/settlements/statements', input);
}
export type ReceiptFile = { fileName: string; fileUrl: string; fileType: string; fileSize: number };
export function uploadSettlementReceipt(file: File) {
  const form = new FormData();
  form.append('file', file);
  return api.postForm<{ url: string }>('/api/v2/uploads', form);
}
export function createCashRecord(input: { partnerType: string; customerId?: string; supplierId?: string; amount: string; occurredAt: string; settlementMonth: string; method?: string; referenceNo?: string; note?: string; receiptFiles?: ReceiptFile[] }) {
  return api.post<{ id: string }>('/api/v2/settlements/cash-records', input);
}
export function createInvoiceRecord(input: { partnerType: string; customerId?: string; supplierId?: string; amount: string; occurredAt: string; settlementMonth: string; invoiceNo?: string; invoiceTitle?: string; taxRate?: string; taxAmount?: string; note?: string; receiptFiles?: ReceiptFile[] }) {
  return api.post<{ id: string }>('/api/v2/settlements/invoice-records', input);
}
export function recordAndAllocate(input: { kind: 'cash' | 'invoice'; partnerType: string; customerId?: string; supplierId?: string; amount: string; occurredAt: string; settlementMonth: string; dateBasis?: string; method?: string; referenceNo?: string; invoiceNo?: string; invoiceTitle?: string; taxRate?: string; taxAmount?: string; note?: string; receiptFiles?: ReceiptFile[] }) {
  return api.post<{ id: string; allocationCount: number; allocatedAmount: number; unallocatedAmount: number }>('/api/v2/settlements/record-and-allocate', input);
}
export function generateStatementPdf(id: string, variant: 'summary' | 'detail' = 'summary') {
  return api.post<{ pdfFileUrl: string; pdfFileName: string; pdfGeneratedAt: string }>(`/api/v2/settlements/statements/${encodeURIComponent(id)}/${variant === 'detail' ? 'detail-pdf' : 'pdf'}`, {});
}
export function refreshStatement(id: string, expectedUpdatedAt: string) {
  return api.post<StatementPayload>(`/api/v2/settlements/statements/${encodeURIComponent(id)}/refresh`, { expectedUpdatedAt });
}
export function changeStatementStatus(id: string, action: StatementAction, reason?: string) {
  return api.post<StatementPayload>(`/api/v2/settlements/statements/${encodeURIComponent(id)}/status`, { action, reason });
}

/**
 * 通用 GET 钩子：换条件时 abort 旧请求（避免竞态），重新取数期间保留旧数据（切月份不闪白）。
 * fresh=false 表示手上的 data 还是上一个条件的结果。
 * （与订单模块的 useResource 同义，多了 query 与 fresh；建议主会话提取成公共钩子）
 */
export function useGet<T>(path: string | null, query?: Q) {
  const url = path ? withQuery(path, query) : null;
  const [nonce, setNonce] = useState(0);
  const [state, setState] = useState<{ data?: T; loading: boolean; error?: Error; url?: string }>({ loading: !!url });
  useEffect(() => {
    if (!url) {
      setState({ loading: false });
      return;
    }
    const c = new AbortController();
    setState((s) => ({ ...s, loading: true, error: undefined }));
    api
      .get<T>(url, undefined, c.signal)
      .then((data) => setState({ data, loading: false, url }))
      .catch((e) => {
        if ((e as Error).name !== 'AbortError') setState((s) => ({ ...s, loading: false, error: e as Error }));
      });
    return () => c.abort();
  }, [url, nonce]);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, fresh: state.url === url, reload, url };
}

/* ---------------- 权限（老系统 role-permission-catalog 里的结算权限码） ---------------- */
export function useStlPerms() {
  const can = useSession((st) => st.can);
  const user = useSession((st) => st.user);
  const followerView = user?.role === 'FOLLOWER' && can('orders.read.assigned') && can('customers.read');
  return {
    overview: can('settlements.overview.read'),
    customer: can('settlements.customer.read') || followerView,
    supplier: can('settlements.supplier.read'),
    statement: can('settlements.statement.read'),
    followerView,
    scope: (s: Scope) => (s === 'customer' && followerView) || can(s === 'supplier' ? 'settlements.supplier.read' : 'settlements.customer.read'),
    cashWrite: (s: Scope) => can(s === 'supplier' ? 'settlements.supplier.cash.write' : 'settlements.customer.cash.write'),
    invoiceWrite: (s: Scope) => can(s === 'supplier' ? 'settlements.supplier.invoice.write' : 'settlements.customer.invoice.write'),
    statementWrite: can('settlements.statement.write'),
    confirm: can('settlements.statement.confirm'),
    lock: can('settlements.statement.lock'),
    pdfWrite: can('settlements.statement.pdf.write'),
    businessExport: can('reports.business.export'),
    allocation: can('settlements.allocation.write'),
  };
}

/* ---------------- 统计区间（写进 URL 的 period 参数：YYYY-MM / YYYY / all） ---------------- */
export type Period = { kind: 'month'; value: string } | { kind: 'year'; value: string } | { kind: 'all' };

function pad(n: number) {
  return String(n).padStart(2, '0');
}
export function thisMonth(now = new Date()) {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
}
export function parsePeriod(v: string | null | undefined, fallback: 'month' | 'all' = 'month'): Period {
  if (v && /^\d{4}-\d{2}$/.test(v)) return { kind: 'month', value: v };
  if (v && /^\d{4}$/.test(v)) return { kind: 'year', value: v };
  if (v === 'all') return { kind: 'all' };
  return fallback === 'all' ? { kind: 'all' } : { kind: 'month', value: thisMonth() };
}
export function periodParam(p: Period, fallback: 'month' | 'all' = 'month') {
  const def = fallback === 'all' ? 'all' : thisMonth();
  const v = p.kind === 'all' ? 'all' : p.value;
  return v === def ? undefined : v;
}
/** 老接口 parseSettlementQuery：month 或 year，二选一；全部时间不传 */
export function periodQuery(p: Period): { month?: string; year?: string } {
  if (p.kind === 'month') return { month: p.value };
  if (p.kind === 'year') return { year: p.value };
  return {};
}
export function periodLabel(p: Period) {
  if (p.kind === 'all') return '全部时间';
  if (p.kind === 'year') return `${p.value} 年`;
  const [y, m] = p.value.split('-');
  return `${y} 年 ${Number(m)} 月`;
}
export function stepPeriod(p: Period, delta: number): Period {
  if (p.kind === 'year') return { kind: 'year', value: String(Number(p.value) + delta) };
  if (p.kind === 'month') {
    const [y, m] = p.value.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    return { kind: 'month', value: `${d.getFullYear()}-${pad(d.getMonth() + 1)}` };
  }
  return p;
}
export function isFuture(p: Period, now = new Date()) {
  if (p.kind === 'month') return p.value >= thisMonth(now);
  if (p.kind === 'year') return Number(p.value) >= now.getFullYear();
  return true;
}
/** 对账期间：月初至月底 */
export function monthRange(period: string) {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  const last = new Date(y, m, 0).getDate();
  return `${y}-${pad(m)}-01 至 ${y}-${pad(m)}-${pad(last)}`;
}

export function defaultBasis(scope: Scope) {
  return DATE_BASIS[scope][0].value;
}

/* ---------------- URL 参数 ---------------- */
export function useUrlState() {
  const [sp, setSp] = useSearchParams();
  const set = useCallback(
    (patch: Record<string, string | undefined | null>) => {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSp],
  );
  return [sp, set] as const;
}

/* ---------------- 老版链接（只读期所有写操作都去老版） ---------------- */
export const legacy = {
  partners: (scope: Scope) => legacyHref(scope === 'supplier' ? '/settlements/suppliers' : '/settlements/customers'),
  /** 老版对账单页认 scope / partnerId / month / dateBasis 四个参数，进去就是这个单位这个月 */
  statements: (q: { scope: Scope; partnerId?: string; month?: string; dateBasis?: string | null }) =>
    legacyHref(withQuery('/settlements/statements', { scope: q.scope, partnerId: q.partnerId, month: q.month, dateBasis: q.dateBasis || undefined })),
  overview: () => legacyHref('/settlements'),
};

/** 业务单据的去处：销售订单进 v2 订单详情，采购单与淘宝单去老版 */
export function documentLink(targetType: string | undefined, id: string): { to?: string; href?: string } {
  if (targetType === 'SALES_ORDER') return { to: `/orders/${id}` };
  if (targetType === 'PURCHASE_ORDER') return { to: `/purchase-orders/${id}` };
  if (targetType === 'TAOBAO_ORDER') return { to: `/taobao/${id}` };
  return {};
}

/* ---------------- 金额显示（底层一律用 lib/format 的 money / num） ---------------- */
/** 负数写成「−￥4,000.00」，不写「￥-4,000.00」 */
export function signedMoney(v: unknown) {
  const n = num(v);
  return n < -0.004 ? `−${money(-n)}` : money(n);
}
export function has(v: unknown): v is number | string {
  return v !== undefined && v !== null;
}

export function executeAutoAllocation(payload: { scope: Scope; partnerId: string; month: string; dateBasis: string; mode: 'cash' | 'invoice' }) { return api.post(`/api/v2/settlements/allocations/auto`, payload); }
